// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'Bathroom Remodeling in Los Angeles, CA 90069',
  description: 'A luxury bathroom renovation in Los Angeles, CA with spa-inspired features, premium tile, and modern lighting — crafted by Modern Build for timeless style and comfort.',
  link: "/services/bathroom-remodeling",
  height: 'h-[467px]',
  image: '/projects/bathroom-remodeling-in-los-angeles-ca-90069/bathroom-remodeling-in-los-angeles-ca-90069.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug = 'bathroom-remodeling-in-los-angeles-ca-90069';
  const galleryPath = path.join(process.cwd(), 'public', 'projects', slug, 'gallery');

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file.replace(/\.(jpg|jpeg|png|webp)$/i, '').replace(/[-_]/g, ' '),
      title: file.replace(/\.(jpg|jpeg|png|webp)$/i, '').replace(/[-_]/g, ' ')
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function BathroomRemodelingLosAngeles({ galleryImages }) {
  const meta = metadata; // Using exported static meta

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta name="keywords" content="bathroom remodeling, Los Angeles, CA 90069, spa bathroom, luxury remodel, Modern Build" />
        <link rel="canonical" href="https://modernbuildremodel.com/projects/bathroom-remodeling-in-los-angeles-ca-90069" />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={`https://modernbuildremodel.com${meta.image}`} />
        <meta property="og:url" content="https://modernbuildremodel.com/projects/bathroom-remodeling-in-los-angeles-ca-90069" />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${meta.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Projects",
                  "item": `https://modernbuildremodel.com/projects`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Bathroom Remodeling In Los Angeles CA 90069",
                  "item": `https://modernbuildremodel.com/projects/bathroom-remodeling-in-los-angeles-ca-90069`
                },
              ]
            })
          }}
        />
      
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Bathroom Remodeling in Los Angeles, CA 90069 | Modern Build',
                description: 'Explore our recent bathroom remodeling project in Los Angeles, CA. View before-and-after images and learn about the materials and design details.',
                slug: '/projects/bathroom-remodeling-in-los-angeles-ca-90069'
              })
            )
          }}
        />
        
      </Head>

      <Header />

      <Hero category="bathroomRemodelingInLosAngelesCA90069" page="page" hasContentH1={false}/>

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="Custom Bathroom Renovation in Los Angeles"
        description="This full bathroom renovation included custom tile work, a floating double vanity, frameless glass walk-in shower, and luxurious lighting upgrades for a spa-like atmosphere. Designed for both beauty and function, every inch was crafted with precision."
        extraText="This Los Angeles home now enjoys a bathroom that’s as stunning as it is practical. We focused on smart space usage, durable materials, and timeless elegance."
        buttonText="Plan My Bathroom Remodel"
      />

      <Gallery images={galleryImages} />
      
      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/wTRS5dkApNM"
            serviceType="Bathroom Remodeling"
            location="Los Angeles, CA"
            description="See the transformation of this outdated bathroom into a luxurious personal retreat with modern amenities."
            serviceLink="/services/bathroom-remodeling"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

<section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      Bathroom Remodeling in Los Angeles, CA 90069
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This luxury bathroom remodel in Los Angeles, CA 90069 transformed an outdated space into a spa-inspired retreat. Every design decision focused on elegance, comfort, and functional luxury.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      Our team combined premium materials, thoughtful layout, and precise craftsmanship to create a bathroom that is both beautiful and practical. For related work, explore our <a
        href="https://modernbuildremodel.com/bathroom-remodeling/california/los-angeles/90069"
        className="text-primary underline"
      >
        Bathroom Remodeling
      </a> or <a
        href="https://modernbuildremodel.com/complete-home-remodeling/california/los-angeles/90069"
        className="text-primary underline"
      >
        Complete Home Remodeling
      </a> services in Los Angeles.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Project Overview
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The original bathroom had outdated finishes, insufficient storage, and inefficient lighting. Our renovation replaced all fixtures and materials with high-end alternatives to deliver a cohesive spa-like atmosphere.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      We installed custom floating vanities, frameless glass shower enclosures, large format tile, and luxurious lighting. Neutral tones and natural textures were used to create a calming, timeless environment.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Craftsmanship and Execution
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      From waterproofing to tile installation and finishing details, every step was executed with precision. This ensures durability, functionality, and a flawless appearance for years to come.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Results and Homeowner Experience
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The remodeled bathroom now functions as a private spa retreat. Enhanced lighting, storage, and luxurious finishes elevate everyday routines into enjoyable experiences.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      This renovation also contributes long-term value and timeless style to the home.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Serving Los Angeles and Surrounding Areas
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Modern Build Home Remodeling proudly serves Los Angeles and neighboring communities with high-end remodeling solutions. Whether you are planning a luxury bathroom upgrade or a full home renovation, our team delivers exceptional results tailored to your lifestyle.
    </p>

  </div>
</section>


      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
